import type { ReactiveController, ReactiveControllerHost } from "lit";
import {
  createActor,
  type ActorRefFrom,
  type SnapshotFrom,
  type Subscription,
} from "xstate";
import type {
  CommerceAppointmentTimeslotSelectionDto,
  CommerceRequestedAction,
  CreateCommerceQuoteDtoOffering,
} from "@zipper/customer-commerce-client";
import type { CheckoutEvent } from "../../../checkout/checkout.machine";
import type {
  CheckoutGateway,
  CheckoutRuntimeIdentity,
} from "../../../checkout/actors";
import {
  surfaceMachine,
  type CommerceSurfaceEvent,
} from "../../../state/surface.machine";

type SurfaceActor = ActorRefFrom<typeof surfaceMachine>;
export type SurfaceSnapshot = SnapshotFrom<typeof surfaceMachine>;

export interface CommerceCheckoutLaunch {
  runtime: CheckoutRuntimeIdentity;
  gateway: CheckoutGateway;
  offering: CreateCommerceQuoteDtoOffering;
  requestedAction: CommerceRequestedAction;
  requestedTargetKey?: string | null;
  quantity?: number;
  customAmountMinor?: number | null;
  timeslots?: readonly CommerceAppointmentTimeslotSelectionDto[];
}

function authorityKey(input: CommerceCheckoutLaunch): string {
  return JSON.stringify({
    orgId: input.runtime.orgId,
    surface: input.runtime.surface,
    audience: input.runtime.audience,
    viewerRevision: input.runtime.viewerRevision,
    customer: input.runtime.customer,
    offering: input.offering,
    requestedAction: input.requestedAction,
    requestedTargetKey: input.requestedTargetKey ?? null,
    quantity: input.quantity ?? 1,
    customAmountMinor: input.customAmountMinor ?? null,
    timeslots: input.timeslots ?? [],
  });
}

/**
 * A restart-safe Lit bridge for the semantic surface actor.
 *
 * React/Lit rerenders with the same authority key preserve the actor and every
 * in-progress draft. A real org/viewer/offering change starts a new protocol;
 * any server intent from the old protocol remains persisted and recoverable.
 */
export class CommerceCheckoutController implements ReactiveController {
  private actor: SurfaceActor | null = null;
  private subscription: Subscription | null = null;
  private checkoutActor: unknown = null;
  private checkoutSubscription: Subscription | null = null;
  private launchKey: string | null = null;
  private gateway: CheckoutGateway | null = null;

  constructor(private readonly host: ReactiveControllerHost) {
    host.addController(this);
  }

  get snapshot(): SurfaceSnapshot | null {
    return this.actor?.getSnapshot() ?? null;
  }

  get checkoutSnapshot() {
    return this.snapshot?.children.checkout?.getSnapshot() ?? null;
  }

  open(input: CommerceCheckoutLaunch): void {
    const nextKey = authorityKey(input);
    if (
      this.actor &&
      this.launchKey === nextKey &&
      this.gateway === input.gateway
    ) {
      return;
    }
    this.stop();
    this.launchKey = nextKey;
    this.gateway = input.gateway;
    this.actor = createActor(surfaceMachine, {
      input: { runtime: input.runtime, gateway: input.gateway },
    });
    this.actor.start();
    this.subscribe();
    this.actor.send({
      type: "OFFERING.OPENED",
      offering: input.offering,
      requestedAction: input.requestedAction,
      ...(input.requestedTargetKey !== undefined
        ? { requestedTargetKey: input.requestedTargetKey }
        : {}),
      ...(input.quantity !== undefined ? { quantity: input.quantity } : {}),
      ...(input.customAmountMinor !== undefined
        ? { customAmountMinor: input.customAmountMinor }
        : {}),
      ...(input.timeslots !== undefined ? { timeslots: input.timeslots } : {}),
    });
  }

  send(event: CheckoutEvent): void {
    this.actor?.send({ type: "CHECKOUT.EVENT", event });
  }

  sendSurface(event: CommerceSurfaceEvent): void {
    this.actor?.send(event);
  }

  hostConnected(): void {
    this.subscribe();
  }

  hostDisconnected(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.checkoutSubscription?.unsubscribe();
    this.checkoutSubscription = null;
    this.checkoutActor = null;
  }

  stop(): void {
    this.hostDisconnected();
    this.actor?.stop();
    this.actor = null;
  }

  private subscribe(): void {
    if (!this.actor || this.subscription) return;
    this.subscription = this.actor.subscribe(() => this.onSurfaceUpdate());
    this.onSurfaceUpdate();
  }

  private onSurfaceUpdate(): void {
    const checkout = this.actor?.getSnapshot().children.checkout ?? null;
    if (checkout !== this.checkoutActor) {
      this.checkoutSubscription?.unsubscribe();
      this.checkoutActor = checkout;
      this.checkoutSubscription =
        checkout?.subscribe(() => this.host.requestUpdate()) ?? null;
    }
    this.host.requestUpdate();
  }
}
